import { useContext } from 'react'

import { AuthContext } from 'contexts'

import { appletListUnion } from 'config/applets'

export const appletGroupList = [
  'covid-19',
  'cardio',
  'influenza',
  'rsv',
  'pad',
  'biorisk',
  'internal-tooling',
  'ida-360',
  'botulism',
] as const
export type appletGroupListUnion = (typeof appletGroupList)[number]

export const appletGroupCategories = ['disease', 'custom', 'business'] as const
export type appletGroupCategoriesUnion = (typeof appletGroupCategories)[number]

export type GroupT = 'Airfinity One ID' | 'Cardiometabolic' | 'Other'

export type AppletGroupConfig = {
  key: appletGroupListUnion
  name: string
  description: string
  category: appletGroupCategoriesUnion
  order: number
  applets: appletListUnion[]
  showApplets: boolean
  hidden?: boolean
  group?: GroupT
}

export const appletGroups: Record<appletGroupListUnion, AppletGroupConfig> = {
  'covid-19': {
    key: 'covid-19',
    name: 'COVID-19',
    description:
      'A complete 360 view on the progression of the COVID-19 pandemic, the political responses, and the landscape of vaccines and therapeutics.',
    category: 'disease',
    order: 1,
    applets: ['covid-science360', 'covid-commercial', 'epidemiology'],
    showApplets: true,
    group: 'Airfinity One ID',
  },
  rsv: {
    key: 'rsv',
    name: 'RSV',
    description:
      'A complete 360 view on seasonal RSV and the latest science, competitive insights, and market analysis for relevant vaccines and therapeutics.',
    category: 'disease',
    order: 2,
    applets: ['rsv-science360', 'rsv-commercial', 'rsv-epidemiology'],
    showApplets: true,
    group: 'Airfinity One ID',
  },

  influenza: {
    key: 'influenza',
    name: 'Influenza',
    description:
      'A complete 360 view on seasonal Influenza and the latest science, competitive insights, and market analysis for relevant vaccines and therapeutics.',
    category: 'disease',
    order: 3,
    applets: [
      'influenza-science360',
      'influenza-commercial',
      'influenza-epidemiology',
    ],
    showApplets: true,
    group: 'Airfinity One ID',
  },
  'ida-360': {
    key: 'ida-360',
    name: 'IDA - Infectious Disease Analytics',
    description:
      'A 360 view of clinical development across all infectious diseases; identify opportunities and track changes with our comprehensive database of candidates, trials, and approvals.',
    category: 'disease',
    order: 4,
    applets: ['ida-360'],
    showApplets: false,
    group: 'Airfinity One ID',
  },
  biorisk: {
    key: 'biorisk',
    name: 'Biorisk',
    description:
      'Monitor pathogenic threats with real time epidemiology tracking, analysis, and more.',
    category: 'disease',
    order: 5,
    applets: ['biorisk'],
    showApplets: false,
    group: 'Airfinity One ID',
  },
  botulism: {
    key: 'botulism',
    name: 'Botulism',
    description:
      'A 360 view of clinical development across Botulism; identify opportunities and track changes with our comprehensive database of candidates, trials, and approvals.',
    category: 'custom',
    order: 17,
    applets: ['botulism'],
    showApplets: false,
    group: 'Other',
  },

  cardio: {
    key: 'cardio',
    name: 'Cardio',
    description:
      'A complete 360 view on drugs in clinical development for a range of Cardiovascular diseases.',
    category: 'disease',
    order: 7,
    applets: ['cardio-science360'],
    showApplets: true,
    group: 'Cardiometabolic',
  },
  pad: {
    key: 'pad',
    name: 'Pandemic Antiviral Discovery',
    description:
      'A complete 360 view of the antiviral treatment landscape in coronaviruses, orthomyxoviruses, and paramyxoviruses, including the latest science and funding efficacy analysis.',
    category: 'custom',
    order: 16,
    applets: ['pad'],
    showApplets: false,
    group: 'Other',
  },
  'internal-tooling': {
    key: 'internal-tooling',
    name: 'Internal Tooling',
    description:
      'Custom app for Airfinity Employees where the Engineering team does live testing of new features or internal tools.',
    category: 'custom',
    order: 17,
    applets: ['internal-tooling'],
    showApplets: false,
    group: 'Other',
  },
}

export const useAppletGroupFromApplet = (appletKey: appletListUnion) => {
  const { appletGroupsUserCanSee } = useContext(AuthContext)

  return Object.values(appletGroupsUserCanSee).find((appletGroup) =>
    appletGroup.applets.includes(appletKey)
  )
}
